<template>
    <div>
      <add-or-update-region-form
        :is-add-or-update-active.sync="isAddOrUpdateActive"
        :update-data="updateData"
        @refetchData="fetchAllData"
        @resetUpdateData="resetUpdateData"
      />
      <h1 class="text-center my-2">Regions</h1>
      <location-code-guide level="region" />
      
      <list-table
        :records="records"
        :columns="tableColumns"
        :total-records="totalRecords"
        :search-filter.sync="search"
        :current-page-number.sync="currentPage"
        :show-search="true"
        :show-filter-by-default="true"
        :show-export-button="can('regions:CanExport')"
        @export="onExport"
      >
        <template #filters>
          <b-row>
            <b-col cols="12" md="3">
              <v-select
                v-model="countryFilter"
                placeholder="All countries"
                :loading="countriesLoading"
                :clearable="true"
                :options="countryOptions"
                :reduce="(val) => val.value"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              />
            </b-col>
            <b-col cols="12" md="3">
              <v-select
                v-model="statusFilter"
                placeholder="Status"
                :clearable="false"
                :options="locationStatusOptions"
                :reduce="(val) => val.value"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              />
            </b-col>
            <b-col cols="12" md="3">
              <v-select
                v-model="sortFilter"
                placeholder="Sort by"
                :clearable="false"
                :options="locationSortOptions"
                :reduce="(val) => val.value"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              />
            </b-col>
          </b-row>
        </template>

        <template v-if="can('regions:CanAddNew')" #action_button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            @click="isAddOrUpdateActive = true"
          >
            <span class="align-middle text-lg">Add Region</span>
          </b-button>
        </template>

        <!-- <template v-if="can('regions:CanUpdate')" #cell(actions)="data">
          <b-button @click="onEdit(data.item)">Edit</b-button>
        </template> -->
        <template #cell(is_active)="data">
          <b-badge pill :variant="isLocationActive(data.item) ? 'light-success' : 'light-secondary'">
            {{ isLocationActive(data.item) ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item v-if="can('regions:CanUpdate')" @click="onEdit(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item v-if="can('regions:CanUpdate')" @click="onToggleStatus(data.item)">
              <feather-icon :icon="isLocationActive(data.item) ? 'EyeOffIcon' : 'EyeIcon'" />
              <span class="align-middle ml-50">{{ isLocationActive(data.item) ? 'Deactivate' : 'Activate' }}</span>
            </b-dropdown-item>

            <b-dropdown-item v-if="can('regions:CanDelete')" @click="onDelete(data.item._id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
    </div>
  </template>
  
  <script>
  import vSelect from 'vue-select'
  import FileDownload from "js-file-download";
  import Ripple from 'vue-ripple-directive'
  import ListTable from "@core/components/ListTable/ListTable.vue";
  import AddOrUpdateRegionForm from "@/@core/components/shared/geo/AddOrUpdateRegionForm.vue"
  import LocationCodeGuide from "@/@core/components/shared/geo/LocationCodeGuide.vue"
  import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
  import LocationSortOptions from "@core/mixins/LocationSortOptions";
  import LocationStatusOptions from "@core/mixins/LocationStatusOptions";
  
  import { debounce, get } from "lodash";
  import { formatDate } from "@core/utils/utils";
  import { BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";
  
  const watchHandler = {
    handler(){
      this.debounceFetchAllData()
    },
    immediate: false
  }
  
  const watchHandlerRequiresPageReset = {
    handler(){
      this.currentPage = 1;
      this.debounceFetchAllData()
    },
    immediate: false
  }
  
  export default {
    components: {
      LocationCodeGuide,
      BRow,
      BCol,
      BLink,
      BModal,
      BMedia,
      BBadge,
      BButton,
      vSelect,
      BAvatar,
      BTooltip,
      ListTable,
      BDropdown,
      BDropdownItem,
      AddOrUpdateRegionForm,
    },
    directives: {
      Ripple,
    },
    mixins: [LocationSortOptions, LocationStatusOptions],
    data() {
      return {
        loading: false,
        records: [],
  
        search: "",
        countryFilter: null,
        countryOptions: [],
        countriesLoading: false,
        updateData: null,
        debounceFetchAllData: null,
        isAddOrUpdateActive: false,
  
        tableColumns: [
          { key: 'title', label: "Region", sortable: false },
          { key: 'country', label: "Nation", sortable: false, formatter: val => (val && val.country) || 'Default (Ghana)' },
          { key: 'code', label: "Code", sortable: false, formatter: val => val || '-' },
          { key: 'is_active', label: "Status", sortable: false },
          { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date' },
          { key: 'actions', sortable: false, class: 'width-200' },
        ]
      }
    },
    computed: {
      perPage: {
        get() {
          return this.$store.getters[`navigation/recordsPerPage`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
        }
      },
      currentPage: {
        get() {
          return this.$store.getters[`navigation/currentPage`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
        }
      },
      totalRecords: {
        get(){
          return this.$store.getters[`navigation/totalRecords`];
        },
        set(value){
          this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
        }
      },
    },
    watch: {
      currentPage: watchHandler,
      search: watchHandlerRequiresPageReset,
      perPage: watchHandlerRequiresPageReset,
      sortFilter: watchHandlerRequiresPageReset,
      statusFilter: watchHandlerRequiresPageReset,
      countryFilter: watchHandlerRequiresPageReset,
    },
    created() {
      this.debounceFetchAllData = debounce(this.fetchAllData, 500);
  
      const {
        search,
        page = 1,
        country_id,
        limit = this.perPage
      } = this.$route.query;

      this.loadCountries(country_id);

      this.restoreLocationSortFromQuery(this.$route.query);
      this.restoreLocationStatusFromQuery(this.$route.query);

      this.search = search;
      this.perPage = +limit;
      this.currentPage = +page;
    },
    methods: {
      // Defaults to Ghana (the default nation, which also holds regions with no nation set).
      // "all" in the URL means the filter was cleared on purpose.
      async loadCountries(from_route) {
        try {
          this.countriesLoading = true;

          const request = await this.useJwt().sharedService.fetchCountries();
          const { data } = request.data;

          this.countryOptions = (data || []).map((item) => ({
            label: item.country,
            value: item._id,
            code: item.iso_code || "",
          }))

          if (from_route === 'all') {
            return;
          }
          if (from_route) {
            this.countryFilter = from_route;
            return;
          }

          const ghana = this.countryOptions.find((option) => option.code.toUpperCase() === 'GH');
          if (ghana) {
            this.countryFilter = ghana.value;
          }
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.countriesLoading = false;
        }
      },
      async onDelete(id){
        try {
          this.loading = true;

          const result = await this.$swal({
            title: 'Confirm Delete?',
            text: "Do you want to delete this record?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed.',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          });

          if (!result.value) {
            return;
          }

          await this.useJwt().sharedService.deleteGeoRegion(id)

          await this.fetchAllData()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckCircleIcon',
              variant: 'success',
              text: 'Record deleted successfully'
            },
          });
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message

          this.$nextTick(() => {
            this.toast({
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'error',
              text: error_message
            });
          })
        } finally {
          this.loading = false;
        }
      },
      async fetchAllData() {
        try {
          this.loading = true;
  
          const query = {
            limit: this.perPage,
            search: this.search,
            page: this.currentPage,
            ...this.locationSortQuery,
            ...this.locationStatusQuery,
          }
          if (this.countryFilter) query.country_id = this.countryFilter;

          // keep a deliberately cleared filter in the URL so a reload does not re-apply Ghana
          await this.$router.push({ query: { ...query, country_id: this.countryFilter || 'all' } }).catch(() => {});
  
          const request = await this.useJwt().sharedService.fetchGeoRegions(query);
          const { data, pagination } = request.data;
  
          this.totalRecords = pagination.totalRecords;
          this.perPage = pagination.limit;
          this.records = data;
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          console.log("error_message", error_message);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.loading = false;
        }
      },
      onToggleStatus(region){
        return this.toggleLocationStatus(region, {
          serviceMethod: "updateGeoRegionStatus",
          label: "Region",
        });
      },
      async onExport() {
        try {
          this.loading = true;

          const result = await this.$swal({
            title: 'Confirm Export?',
            text: "This will export data based on current search and filters.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed.',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          });

          if (!result.value) {
            return;
          }

          const query = {
            search: this.search,
            ...this.locationSortQuery,
            ...this.locationStatusQuery,
          }
          if (this.countryFilter) query.country_id = this.countryFilter;

          const response = await this.useJwt().sharedService.exportGeoRegions(query);
          FileDownload(response.data, "regions-export.xlsx");
        } catch (error) {
          const error_message = get(error, "response.statusText") || get(error, "response.data.message") || error.message;

          this.$nextTick(() => {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error!',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error_message
              },
            });
          })
        } finally {
          this.loading = false;
        }
      },
      onEdit(update){
        this.updateData = update;
        this.isAddOrUpdateActive = true;
      },
      resetUpdateData(){
        if (this.updateData){
          this.updateData = null
        }
      },
    }
  }
  </script>
  

  


